import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { v4 } from 'uuid';
import { Box, Grid, Typography, DialogTitle, DialogContent, Button, Collapse, Alert, AlertTitle } from '@mui/material';
import { RoundedDialog, StandardDialogActions } from '../../../Global/Components';
import StateManager from '../../../Global/StateManager';
import axios from 'axios';

import { DataGridPremium, GridRowModes, GridActionsCellItem, GridRowEditStopReasons } from '@mui/x-data-grid-premium';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Dropzone from '../../../Hubs/dochub/components/Dropzone';
import { acceptedExcelFormats } from '../../../Global/FilePreviewer/types';

function Footer(props) {
  return (
    <Box sx={{ p: 1, display: 'flex' }}>
      {!props.isValid && (
        <Typography variant="body2" color="error">
          {props.error}
        </Typography>
      )}
    </Box>
  );
}

function Header({ limitExceeded }) {
  const [instructions, setInstructions] = useState(false);
  const downloadSampleSheet = () => {
    const data = [
      ['First Name', 'Last Name', 'Email', 'Username', 'Password', 'Mobile', 'Job Role', 'User Type'],
      ['John', 'Doe', 'john@doe.com', undefined, '123456789@Aa', '', 'Manager', 'admin'],
      ['Jane', 'Doe', undefined, 'jane', '123456789@aA', '', 'Manager', 'standard'],
      ['Tom', 'Doe', 'tom@doe.com', undefined, '123456789@aA', '', 'Manager', 'portal'],
      ['Jerry', 'Doe', undefined, 'jerry', '123456789@aA', '', 'Manager', 'view'],
    ];
    import('xlsx').then((XLSX) => {
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, 'SheetJS');
      XLSX.writeFile(wb, 'sample.xlsx');
    });
  };

  return (
    <Grid container direction="column" sx={{ p: 2 }}>
      {limitExceeded && (
        <Grid container item>
          <Alert severity="warning" sx={{ width: '100%', borderRadius: 1 }}>
            <AlertTitle>
              You have exceeded your purchased user limit. Please purchase more users or remove paid users from the list
              below to continue.
            </AlertTitle>
          </Alert>
        </Grid>
      )}
      <Grid container item justifyContent="space-between" sm>
        <Button onClick={downloadSampleSheet}>Download Sample Sheet </Button>
        <Button
          onClick={() => setInstructions(!instructions)}
          endIcon={instructions ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        >
          {instructions ? 'Hide Instructions' : 'Show Instructions'}
        </Button>
      </Grid>
      <Collapse in={instructions}>
        <Grid container sx={{ my: 2 }}>
          <Alert severity="info" sx={{ width: '100%', borderRadius: 1 }}>
            <AlertTitle>
              To ensure that user are imported correctly, please ensure you follow the instructions below:
            </AlertTitle>
            <Typography sx={{ fontSize: 14 }}>
              Email and Username can not be filled in at the same time in a row
            </Typography>
            <Typography sx={{ fontSize: 14 }}>
              User Type can only be one of these: "admin, standard, view, portal"
            </Typography>
            <Typography sx={{ fontSize: 14 }}>
              Columns order is required for the row to be processed properly.
            </Typography>
            <Typography sx={{ fontSize: 14 }}>If a password is provided it must adhere to the following:</Typography>
            <Typography sx={{ fontSize: 14, mx: 2 }}>Password be at least 8 characters long</Typography>
            <Typography sx={{ fontSize: 14, mx: 2 }}>Contain at least 1 lowercase letter</Typography>
            <Typography sx={{ fontSize: 14, mx: 2 }}>Contain at least 1 uppercase letter</Typography>
            <Typography sx={{ fontSize: 14, mx: 2 }}>Contain at least 1 special character</Typography>
          </Alert>
        </Grid>
      </Collapse>
    </Grid>
  );
}

const ImportUsersDialog = ({ open, onClose, onResult }) => {
  const [data, setData] = useState([]);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const { company } = useSelector(({ admin }) => admin);
  const [paidUsers, setPaidUsers] = useState(0);

  const limitExceeded = company?.consumedUsers + paidUsers > company?.purchasedUsers;

  const [rowModesModel, setRowModesModel] = useState({});
  const [selectedRow, setSelectedRow] = useState([]);

  const invalidRows = data.filter((row) => !row.isValid);

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id) => () => {
    setData(data.filter((row) => row.id !== id));
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = data.find((row) => row.id === id);
    if (editedRow.isNew) {
      setData(data.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = (newRow) => {
    const updatedRow = { ...newRow, error: '', isValid: false, isNew: false };
    setData(data.map((row) => (row.id === newRow.id ? updatedRow : row)));
    processValidation(data.map((row) => (row.id === newRow.id ? updatedRow : row)));
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  function onSave() {
    setSaving(true);
    axios
      .post('/admin/management/importUsers', { users: data })
      .then((res) => {
        setSaving(false);
        onResult(res.data);
        StateManager.setSuccessAlert('Users imported successfully');
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
        setSaving(false);
      });
  }

  async function handleFileDrop(file) {
    //read xlsx from file
    const fb = await file.arrayBuffer();
    import('xlsx').then((XLSX) => {
      const wb = XLSX.read(fb, { type: 'buffer' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
      // remove header
      rows.shift();
      // foramt data
      const newOptions = rows.map((x) => ({
        id: v4(),
        firstName: x[0],
        lastName: x[1],
        email: x[2],
        username: x[3],
        presetPassword: x[4],
        mobile: x[5],
        jobRole: x[6],
        access: x[7] !== undefined ? x[7].toLowerCase() : 'view',
        type: x[2] !== undefined ? 'email' : 'username',
        isValid: !['standard', 'portal', 'view', 'admin'].includes(x[7] || (x[3] !== undefined && x[4] === undefined)),
        error: '',
      }));
      let result = [...data, ...newOptions];
      // remove duplicates
      // result = result.filter((x, i, a) => a.findIndex((y) => y.email === x.email || y.username === x.username) === i);
      const paid = result.filter((x) => x.access === 'standard' || x.access === 'admin');
      setPaidUsers(paid.length);
      setData(result);
      processValidation(result?.filter((x) => (x.email !== undefined && x.username !== undefined ? false : x)));
    });
  }

  const processValidation = (data) => {
    setValidating(true);
    axios
      .post('/admin/management/validateUsers', { users: data })
      .then((res) => {
        setValidating(false);
        const result = data.map((x) => {
          const user = res.data.find((y) => y.id === x.id);
          return { ...x, ...user };
        });
        setData(result);
        if (selectedRow) {
          // update selectedRow status
          const exists = result?.find((x) => x.id === selectedRow?.id);
          setSelectedRow(exists);
        }
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
        setValidating(false);
      });
  };

  const columns = [
    {
      field: 'firstName',
      headerName: 'First name',
      editable: true,
      display: 'flex',
    },
    {
      field: 'lastName',
      headerName: 'Last name',
      editable: true,
      display: 'flex',
    },
    {
      field: 'email',
      headerName: 'Email',
      editable: true,
      display: 'flex',
    },
    {
      field: 'username',
      headerName: 'Username',
      editable: true,
      display: 'flex',
    },
    {
      field: 'presetPassword',
      headerName: 'Password',
      editable: true,
      display: 'flex',
    },
    {
      field: 'mobile',
      headerName: 'Mobile',
      editable: true,
      display: 'flex',
    },
    {
      field: 'jobRole',
      headerName: 'Job role',
      editable: true,
      display: 'flex',
    },
    {
      field: 'access',
      headerName: 'User Type',
      editable: true,
      type: 'singleSelect',
      display: 'flex',
      valueOptions: ['admin', 'standard', 'view', 'portal'],
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      display: 'flex',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{
                color: 'primary.main',
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem icon={<DeleteIcon />} label="Delete" onClick={handleDeleteClick(id)} color="inherit" />,
        ];
      },
    },
  ];

  useEffect(() => {
    if (!open) return;
    setData([]);
    setSaving(false);
    setValidating(false);
    setRowModesModel({});
    setSelectedRow([]);
  }, [open]);

  return (
    <RoundedDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Import Users</DialogTitle>
      <DialogContent>
        <Grid container>
          <Grid item sm>
            <Dropzone onFileDrop={handleFileDrop} accept={acceptedExcelFormats} />
          </Grid>
        </Grid>
        <Box sx={{ height: 520, width: '100%', my: 2 }}>
          <DataGridPremium
            disableAggregation
            disableRowGrouping
            loading={validating || saving}
            rows={data || []}
            columns={columns}
            rowHeight={38}
            getRowId={(row) => row.id}
            editMode="row"
            rowModesModel={rowModesModel}
            onRowClick={(params) => setSelectedRow(params.row)}
            onRowModesModelChange={handleRowModesModelChange}
            onRowEditStop={handleRowEditStop}
            processRowUpdate={processRowUpdate}
            getRowClassName={({ row }) => {
              if (!row.isValid) {
                return 'invalid-row';
              }
              return '';
            }}
            slots={{
              footer: Footer,
              toolbar: Header,
            }}
            slotProps={{
              footer: {
                isValid: data.every((row) => row.isValid),
                error: selectedRow?.error || `${invalidRows.length} row(s) have invalid fields`,
              },
              limitExceeded,
            }}
          />
        </Box>
      </DialogContent>
      <StandardDialogActions
        onClose={onClose}
        onDone={onSave}
        saving={saving}
        hideDone={data?.length === 0 || (data?.length > 0 && data?.some((row) => !row.isValid))}
        disableDone={limitExceeded}
      />
    </RoundedDialog>
  );
};

export default ImportUsersDialog;
